import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  EventEmitter,
  input,
  Output,
  signal,
} from '@angular/core';
import { DrawingHistory } from '../../../types/drawing/drawing-history.type';
import { DrawingLine } from '../../../types/drawing/drawing-line.type';
import { DrawingPosition } from '../../../types/drawing/drawing-position.type';
import { DrawingMode } from '../../../types/drawing/drawing.mode.type';

const CANVAS_ID = 'canvas';
const CANVAS_TEMP_ID = 'canvas-temp';

@Component({
  selector: 'infl-drawing-canvas',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './drawing-canvas.component.html',
  styleUrl: './drawing-canvas.component.scss',
})
export class DrawingCanvasComponent implements AfterViewInit {
  public thickness = input.required<number>();
  public color = input.required<string>();
  public opacity = input.required<number>();
  public mode = input.required<DrawingMode>();
  public history = input<DrawingHistory>([]);

  public height = input<number>(null);

  private _height = signal(0);
  private _width = signal(0);

  protected currentHeight = computed(() => {
    return this.height() || this._height();
  });

  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;

  private canvasTemp: HTMLCanvasElement;
  private ctxTemp: CanvasRenderingContext2D;

  protected position: DrawingPosition = { x: null, y: null };
  private lastPosition: DrawingPosition = { x: null, y: null };

  private pencilsPoints: DrawingLine = null;

  private canvasHistory: DrawingHistory = [];
  private currentHistoryIndex = 0;

  protected isDrawing = false;

  private get isErasing(): boolean {
    return this.mode() === 'eraser';
  }

  private getDistanceX(x: number): number {
    if (!this.lastPosition.x) {
      return 0;
    }
    return Math.abs(x - this.lastPosition.x);
  }
  private getDistanceY(y: number): number {
    if (!this.lastPosition.y) {
      return 0;
    }
    return Math.abs(y - this.lastPosition.y);
  }
  private getDistancePoints(x: number, y: number): number {
    return this.getDistanceX(x) + this.getDistanceY(y);
  }

  @Output() drawingChange = new EventEmitter<boolean>();
  @Output() historyChange = new EventEmitter<DrawingHistory>();

  constructor() {
    effect(() => {
      this.updateContexts();
      this.applyHistory(this.history());
    });
  }

  ngAfterViewInit(): void {
    try {
      const parent = (document.getElementById(CANVAS_ID) as HTMLCanvasElement)
        .parentElement;
      this._height.set(parent.clientHeight);
      this._width.set(parent.clientWidth);
    } catch (e) {
      console.error(e);
    }

    this.canvas = document.getElementById(CANVAS_ID) as HTMLCanvasElement;
    this.canvas.height = this.currentHeight();
    this.canvas.width = this._width();
    this.ctx = this.canvas.getContext('2d') as CanvasRenderingContext2D;

    this.canvasTemp = document.getElementById(
      CANVAS_TEMP_ID,
    ) as HTMLCanvasElement;
    this.canvasTemp.height = this.currentHeight();
    this.canvasTemp.width = this._width();
    this.ctxTemp = this.canvasTemp.getContext('2d') as CanvasRenderingContext2D;

    this.applyHistory(this.history());
    this.updateContexts();
  }

  public getImage(): string {
    return this.canvas.toDataURL('image/png');
  }

  public delete(): void {
    this.ctx.clearRect(0, 0, this._width(), this.currentHeight());
    this.canvasHistory = [];
    this.currentHistoryIndex = 0;
    this.historyChange.emit(this.canvasHistory);
  }

  protected onTouchStart($event: TouchEvent): void {
    $event.preventDefault();
    this.drawingChange.emit(true);
    const touch = $event.touches[0];
    const rect = this.canvasTemp.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;
    this.isDrawing = true;
    this.updatePosition(x, y);
    this.onPaint();
  }

  protected onTouchMove($event: TouchEvent): void {
    const touch = $event.touches[0];
    const rect = this.canvasTemp.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;
    if (this.getDistancePoints(x, y) > 300) {
      return;
    }
    this.updatePosition(x, y);
    this.onPaint();
  }

  protected onTouchEnd(): void {
    this.drawingChange.emit(false);
    this.isDrawing = false;
    this.ctx.drawImage(this.canvasTemp, 0, 0);
    this.clearTempCanvas();
    this.resetPositions();
  }

  private onPaint(): void {
    if (!this.isDrawing) {
      return;
    }
    this.updatePencilsPoints();
    this.draw(this.isErasing);
  }

  private draw(isErasing: boolean): void {
    const contextTarget = isErasing ? this.ctx : this.ctxTemp;

    if (this.pencilsPoints.positions.length < 3) {
      const start = this.pencilsPoints.positions.at(0);
      const arcWidth = this.ctxTemp.lineWidth / 2;
      contextTarget.beginPath();
      contextTarget.arc(start.x, start.y, arcWidth, 0, Math.PI * 2, !0);
      contextTarget.fill();
      contextTarget.closePath();
      return;
    }

    const pos = this.pencilsPoints.positions;

    this.clearTempCanvas();
    contextTarget.beginPath();
    contextTarget.moveTo(pos.at(0).x, pos.at(0).y);

    let lastIndex = 1;
    for (let i = 1; i < this.pencilsPoints.positions.length - 2; i++) {
      const c = (pos.at(i).x + pos.at(i + 1).x) / 2;
      const d = (pos.at(i).y + pos.at(i + 1).y) / 2;
      contextTarget.quadraticCurveTo(pos.at(i).x, pos.at(i).y, c, d);
      lastIndex = i;
    }

    // For the last 2 points
    contextTarget.quadraticCurveTo(
      pos.at(lastIndex).x,
      pos.at(lastIndex).y,
      pos.at(lastIndex + 1).x,
      pos.at(lastIndex + 1).y,
    );
    contextTarget.stroke();
  }

  private updatePosition(x: number, y: number): void {
    if (!this.isDrawing) {
      return;
    }
    this.lastPosition.x = this.position.x;
    this.lastPosition.y = this.position.y;

    this.position.x = x;
    this.position.y = y;
  }

  private resetPositions(saveHistory = true): void {
    this.position.x = null;
    this.position.y = null;
    this.lastPosition.x = null;
    this.lastPosition.y = null;
    if (saveHistory) {
      this.addToHistory();
    }
    this.pencilsPoints = this.defaultPencilsPoints();
  }

  public undo(): void {
    if (this.canvasHistory.length === 0) {
      return;
    }
    if (this.currentHistoryIndex === 0) {
      return;
    }
    this.currentHistoryIndex--;
    this.drawHistory();
  }

  public redo(): void {
    if (this.canvasHistory.length === this.currentHistoryIndex) {
      return;
    }
    this.currentHistoryIndex++;
    this.drawHistory();
  }

  private drawHistory(): void {
    if (!this.ctx || !this.ctxTemp) return;

    this.ctx.clearRect(0, 0, this._width(), this.currentHeight());
    for (let i = 0; i < this.currentHistoryIndex; i++) {
      this.pencilsPoints = this.canvasHistory[i];
      [this.ctx, this.ctxTemp].forEach((context) => {
        context.strokeStyle = `${this.pencilsPoints.color}`;
        context.fillStyle = `${this.pencilsPoints.color}`;
        context.lineWidth = this.pencilsPoints.thickness;
        context.globalAlpha = this.pencilsPoints.opacity;
        context.lineCap = 'round';
        context.lineJoin = 'round';
        context.globalCompositeOperation =
          this.pencilsPoints.mode === 'eraser'
            ? 'destination-out'
            : 'source-over';
      });
      this.draw(this.pencilsPoints.mode === 'eraser');
      this.ctx.drawImage(this.canvasTemp, 0, 0);
      this.ctxTemp.clearRect(0, 0, this._width(), this.currentHeight());
      this.resetPositions(false);
    }
    this.updateContexts();
    this.pencilsPoints = this.defaultPencilsPoints();
  }

  private defaultPencilsPoints(): DrawingLine {
    return {
      positions: [],
      color: this.color(),
      thickness: this.thickness(),
      mode: this.mode(),
      opacity: this.opacity(),
    };
  }

  private updatePencilsPoints(): void {
    this.pencilsPoints.positions.push({ ...this.position });
    this.pencilsPoints.color = this.color();
    this.pencilsPoints.thickness = this.thickness();
    this.pencilsPoints.mode = this.mode();
    this.pencilsPoints.opacity = this.opacity();
  }

  private addToHistory(): void {
    if (this.currentHistoryIndex < this.canvasHistory.length) {
      this.canvasHistory.splice(
        this.currentHistoryIndex,
        this.canvasHistory.length,
      );
    }
    this.canvasHistory.push(this.pencilsPoints);
    this.currentHistoryIndex++;
    this.historyChange.emit(this.canvasHistory);
  }

  private updateContexts(): void {
    if (!this.ctx || !this.ctxTemp) return;

    [this.ctx, this.ctxTemp].forEach((context) => {
      context.strokeStyle = `${this.color()}`;
      context.fillStyle = `${this.color()}`;
      context.lineWidth = this.thickness();
      context.globalAlpha = this.opacity();
      context.lineCap = 'round';
      context.lineJoin = 'round';
      context.globalCompositeOperation =
        this.mode() === 'eraser' ? 'destination-out' : 'source-over';
    });
    this.pencilsPoints = this.defaultPencilsPoints();
  }

  private clearTempCanvas(): void {
    const width = this.canvasTemp.width;
    const height = this.canvasTemp.height;
    this.ctxTemp.clearRect(0, 0, width, height);
  }

  private applyHistory(history: DrawingHistory): void {
    if (history) {
      this.canvasHistory = history;
      this.currentHistoryIndex = history.length;
      this.drawHistory();
    }
  }

  protected onMouseDown($event: MouseEvent) {
    this.isDrawing = true;
    this.drawingChange.emit(true);
    this.drawByMouse($event);
  }

  protected onMouseMove($event: MouseEvent) {
    this.drawByMouse($event);
  }

  private drawByMouse($event: MouseEvent): void {
    if (!this.isDrawing) {
      return;
    }
    const rect = this.canvasTemp.getBoundingClientRect();
    const x = $event.clientX - rect.left;
    const y = $event.clientY - rect.top;
    this.updatePosition(x, y);
    this.onPaint();
  }
}
